import { computed, onBeforeUnmount, ref } from 'vue';
import {
    deleteMaintenanceDraft,
    getMaintenanceDraft,
    getMaintenanceOutboxForDraft,
    putMaintenanceDraft,
    putMaintenanceOutbox,
    requestPersistentMaintenanceStorage,
} from './maintenanceDb';
import {
    MAINTENANCE_OUTBOX_STATES,
    maintenancePhotoIdsFromPayload,
    shouldRetryMaintenanceSync,
    syncMaintenanceOutboxEntry,
} from './maintenanceSync';

export function useMaintenanceOfflineForm({ scope, draftKey, sheetName, endpoint }) {
    const outbox = ref(null);
    const status = ref('idle');
    const lastError = ref(null);
    const lastResponse = ref(null);
    const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine);
    const syncing = computed(() => [
        MAINTENANCE_OUTBOX_STATES.uploading,
        MAINTENANCE_OUTBOX_STATES.submitting,
    ].includes(status.value));
    const pending = computed(() => Boolean(outbox.value) && status.value !== MAINTENANCE_OUTBOX_STATES.synced);

    const handleOnline = () => {
        online.value = true;
        if (shouldRetryMaintenanceSync(outbox.value)) sync().catch(() => { });
    };
    const handleOffline = () => { online.value = false; };

    async function initialize() {
        await requestPersistentMaintenanceStorage();
        const [draft, queued] = await Promise.all([
            getMaintenanceDraft(scope, draftKey),
            getMaintenanceOutboxForDraft(scope, draftKey),
        ]);
        outbox.value = queued ?? null;
        status.value = queued?.state ?? (draft ? 'draft' : 'idle');
        lastError.value = queued?.lastError ?? null;

        if (typeof window !== 'undefined') {
            window.addEventListener('online', handleOnline);
            window.addEventListener('offline', handleOffline);
        }

        return draft ?? null;
    }

    async function saveDraft(data, clientUuid) {
        const draft = await putMaintenanceDraft(scope, {
            key: draftKey,
            sheetName,
            clientUuid,
            data,
            photoIds: maintenancePhotoIdsFromPayload(data),
        });
        if (!outbox.value) status.value = 'draft';
        return draft;
    }

    async function queue(data, clientUuid, headers = {}) {
        await saveDraft(data, clientUuid);
        outbox.value = await putMaintenanceOutbox(scope, {
            ...outbox.value,
            draftKey,
            operation: 'create',
            sheetName,
            endpoint,
            payload: data,
            photoIds: maintenancePhotoIdsFromPayload(data),
            photoRelation: 'photos',
            clientUuid,
            idempotencyKey: clientUuid,
            headers,
            state: MAINTENANCE_OUTBOX_STATES.pending,
            lastError: null,
        });
        status.value = outbox.value.state;
        lastError.value = null;

        if (!online.value) return { queued: true, synced: false };
        return sync();
    }

    async function sync() {
        if (!outbox.value || syncing.value || !online.value) {
            return { queued: Boolean(outbox.value), synced: false };
        }

        status.value = MAINTENANCE_OUTBOX_STATES.uploading;
        try {
            const result = await syncMaintenanceOutboxEntry(scope, outbox.value);
            status.value = result.state;
            lastResponse.value = result.response;
            outbox.value = null;
            lastError.value = null;
            return { queued: false, synced: true, response: result.response };
        } catch (error) {
            outbox.value = await getMaintenanceOutboxForDraft(scope, draftKey);
            status.value = outbox.value?.state ?? MAINTENANCE_OUTBOX_STATES.failed;
            lastError.value = outbox.value?.lastError ?? { message: error.message };
            return { queued: true, synced: false, error };
        }
    }

    async function discard() {
        await deleteMaintenanceDraft(scope, draftKey, { deletePhotos: true });
        outbox.value = null;
        status.value = 'idle';
        lastError.value = null;
    }

    onBeforeUnmount(() => {
        if (typeof window !== 'undefined') {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        }
    });

    return {
        outbox,
        status,
        lastError,
        lastResponse,
        online,
        syncing,
        pending,
        initialize,
        saveDraft,
        queue,
        sync,
        discard,
    };
}